import { ForwardedRef, forwardRef } from 'react'
import { cn } from '~utils/cn'

import TextInputHelper, { TextInputHelperProps } from './@base/helper'
import { TextAreaPrimitiveProps, useCustomizedInputProps } from './@base/hooks'
import TextInputLabel, { TextInputLabelProps } from './@base/label'

type CommonProps = {
  error: boolean
  helperMessage: TextInputHelperProps['helperMessage']
  showHelper: boolean
  showMaxLength: boolean
}

type BoxTypeProps = CommonProps & {
  type: 'box'
  label: TextInputLabelProps['children']
}

type OutlineTypeProps = CommonProps & {
  type: 'outline'
  label: never
}

type TextAreaProps = Readonly<TextAreaPrimitiveProps & Partial<BoxTypeProps | OutlineTypeProps>>

/**
 * @version v1.2.0
 * @author Maggie
 * @description TextInputHelper style 잘못 나오는 부분 수정
 */
const TextArea = forwardRef(
  (
    {
      type = 'box',
      label,
      helperMessage,
      showHelper = false,
      showMaxLength = false,
      error,
      ...props
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const { isFocused, inputProps } = useCustomizedInputProps<HTMLTextAreaElement, 'textarea'>(
      props
    )
    const { value, disabled, readOnly, maxLength, className } = inputProps

    const valueLength = value?.toString()?.length

    return (
      <div className="flex flex-col">
        {type === 'box' && label && (
          <TextInputLabel isFocused={isFocused} isError={error} isDisabled={disabled}>
            {label}
          </TextInputLabel>
        )}
        <textarea
          {...inputProps}
          ref={ref}
          className={cn(
            'h-full w-full',
            'no-scrollbar resize-none border border-solid border-white outline-none',
            'transition-colors motion-reduce:transition-none',
            'text-gray-900 text-body-1 placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'dark:border-gray-800',
            type === 'box' &&
              cn('rounded-xl bg-white dark:bg-gray-800 p-4 dark:text-white', {
                'border-gray-200 dark:border-gray-800': isFocused,
                'border-red-100': error,
                'bg-gray-50 text-gray-300': disabled || readOnly,
              }),
            type === 'outline' &&
              cn('bg-background text-gray-900', {
                'text-gray-900': isFocused,
                'text-red-500': error,
                'text-gray-300': readOnly || disabled,
              }),
            className
          )}
        />
        {(showHelper || showMaxLength) && (
          <TextInputHelper
            helperMessage={helperMessage}
            maxLength={maxLength}
            showMaxLength={showMaxLength}
            valueLength={valueLength}
            isError={error}
            isDisabled={disabled}
            readOnly={readOnly}
            className="mt-1"
          />
        )}
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'
export default TextArea
